import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Box, CssBaseline, ThemeProvider, Paper, CircularProgress, Alert } from '@mui/material';
import MapComponent from '../components/MapComponent';
import SpaceDetailsPanel from '../components/SpaceDetailsPanel';
import StatsDashboard from '../components/StatsDashboard';
import brandTheme from '../theme';
import Logo from '../assets/logo_bms.png';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/espacios';

export default function SharedViewPage() {
  const { shareId } = useParams();
  const [espacios, setEspacios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'deactivated' | 'notfound'
  const [linkName, setLinkName] = useState('Vista Compartida');
  const [selectedSpaceOnMap, setSelectedSpaceOnMap] = useState(null);
  const [mapsApiKey, setMapsApiKey] = useState(null);

  const fetchSharedConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorType(null);

      const API_ROOT = API_BASE_URL.replace(/\/api\/espacios.*$/, '');
      const response = await fetch(`${API_ROOT}/api/share/${shareId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setErrorType('notfound');
          throw new Error('Este enlace no existe o ha sido eliminado.');
        } else if (response.status === 403) {
          setErrorType('deactivated');
          throw new Error('Este enlace ha sido desactivado.');
        } else if (response.status === 410) {
          setErrorType('expired');
          throw new Error('Este enlace ha expirado.');
        }
        throw new Error(`Error del servidor: ${response.status}`);
      }

      const configData = await response.json();
      if (configData.name) setLinkName(configData.name);

      // Now fetch the actual filtered spaces via the new public endpoint
      const spacesResponse = await fetch(`${API_ROOT}/api/share/${shareId}/espacios`);
      if (!spacesResponse.ok) throw new Error('No se pudieron cargar los espacios.');
      const data = await spacesResponse.json();
      
      setEspacios(data);
      if (data.length > 0) setSelectedSpaceOnMap(data[0]);

      // Fetch Maps API Key
      try {
        const keyResponse = await fetch(`${API_ROOT}/api/config/maps-key`);
        if (keyResponse.ok) {
          const keyData = await keyResponse.json();
          setMapsApiKey(keyData.apiKey);
        }
      } catch (keyErr) {
        console.error("Error fetching Maps API Key:", keyErr);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Enlace no válido o expirado.');
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  useEffect(() => { fetchSharedConfig(); }, [fetchSharedConfig]);
  const handleSpaceSelect = useCallback((space) => setSelectedSpaceOnMap(space), []);

  return (
    <ThemeProvider theme={brandTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw', overflow: 'hidden' }}>
        <AppBar position="static" color="primary" elevation={0}>
          <Toolbar>
            <img src={Logo} alt="Billboard Hub Logo" style={{ height: '40px', marginRight: '14px' }} />
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
              {linkName}
            </Typography>
            {!loading && !error && (
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {espacios.length} espacio{espacios.length !== 1 ? 's' : ''} disponible{espacios.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Toolbar>
        </AppBar>

        {loading ? (
          <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 2 }}>
            <CircularProgress />
            <Typography color="text.secondary">Cargando vista compartida...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
            <Alert
              severity={errorType === 'deactivated' || errorType === 'expired' ? 'warning' : 'error'}
              sx={{ maxWidth: 480, borderRadius: 2 }}
            >
              <Typography fontWeight={700} mb={0.5}>
                {errorType === 'deactivated' ? 'Enlace desactivado' : errorType === 'expired' ? 'Enlace expirado' : 'Enlace no encontrado'}
              </Typography>
              {error}
            </Alert>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
            <Paper elevation={0} sx={{ width: '300px', p: 2.5, overflowY: 'auto', borderRight: '1px solid #eee' }}>
              <StatsDashboard espacios={espacios} />
              <Box sx={{ my: 2, borderBottom: '1px solid #eee' }} />
              <SpaceDetailsPanel selectedSpace={selectedSpaceOnMap} />
            </Paper>
            <Box sx={{ flexGrow: 1 }}>
              {mapsApiKey ? (
                <MapComponent
                  espacios={espacios}
                  onSpaceSelect={handleSpaceSelect}
                  selectedSpaceFromApp={selectedSpaceOnMap}
                  mapsApiKey={mapsApiKey}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={30} />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
}
